import { T, useGT, useLocale } from 'gt-react';
import {
  ArrowArcLeftIcon,
  ArrowArcRightIcon,
  ClockCounterClockwiseIcon,
} from '@phosphor-icons/react';
import { IconTooltip } from '@/components/common/IconTooltip';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { historyGraph } from '@/lib/undoTree';
import type { UndoTreeControls } from '@/lib/useUndoTree';
import { cn } from '@/lib/utils';
import { HistoryGraph } from './HistoryGraph';

/**
 * Undo, Redo, and the history as a graph: every state the editor reached,
 * newest on top, joined to the state it was made from, with each branch made
 * after an undo in a lane of its own. Picking a row jumps to that state; none
 * is ever lost.
 */
export function UndoHistoryControls<S>({
  controls,
  label,
  showNavigation = true,
  disabled = false,
}: {
  controls: UndoTreeControls<S>;
  /** Names the history, to tell it apart from another editor's on screen. */
  label: string;
  /** A host with existing undo/redo buttons can show only the popover. */
  showNavigation?: boolean;
  disabled?: boolean;
}) {
  const gt = useGT();
  const locale = useLocale();
  const time = new Intl.DateTimeFormat(locale, { timeStyle: 'medium' });
  const graph = historyGraph(controls.tree);
  return (
    <div role="group" aria-label={label} className="flex items-center">
      {showNavigation && (
        <>
          <IconTooltip
            label={gt('Undo (⌘Z)', { context: 'undo history button tooltip' })}
          >
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              disabled={disabled || !controls.canUndo}
              onClick={controls.undo}
              aria-label={gt('Undo', { context: 'undo history button' })}
            >
              <ArrowArcLeftIcon className="h-4 w-4" />
            </Button>
          </IconTooltip>
          <IconTooltip
            label={gt('Redo (⇧⌘Z)', { context: 'undo history button tooltip' })}
          >
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              disabled={disabled || !controls.canRedo}
              onClick={controls.redo}
              aria-label={gt('Redo', { context: 'undo history button' })}
            >
              <ArrowArcRightIcon className="h-4 w-4" />
            </Button>
          </IconTooltip>
        </>
      )}
      <Popover>
        <IconTooltip
          label={gt('Edit history', { context: 'undo history button' })}
        >
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              disabled={disabled}
              aria-label={gt('Edit history', {
                context: 'undo history button',
              })}
            >
              <ClockCounterClockwiseIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
        </IconTooltip>
        <PopoverContent align="end" className="w-80 p-0">
          <p className="border-b px-3 py-2 text-xs font-medium">
            <T context="undo history title">Edit history</T>
          </p>
          <HistoryGraph
            graph={graph}
            rowKey={(row) => row.node.seq}
            label={gt('Edit history', { context: 'undo history list' })}
            className="max-h-80 overflow-y-auto py-1"
            renderRow={(row, cell) => (
              <button
                type="button"
                disabled={disabled}
                aria-current={row.isCurrent ? 'step' : undefined}
                onClick={() => controls.jumpTo(row.node.seq)}
                className={cn(
                  'flex w-full items-center gap-2 px-3 text-left text-xs hover:bg-muted',
                  !row.isActive && 'text-muted-foreground',
                )}
              >
                {cell}
                <span className="min-w-0 flex-1 truncate">
                  {row.node.label}
                </span>
                <span className="shrink-0 tabular-nums text-muted-foreground">
                  {time.format(row.node.at)}
                </span>
              </button>
            )}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
